import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import classnames from "classnames";
import { useCurrentRefinements } from "react-instantsearch";
import useIsMobile from "../hooks/useIsMobile";

const pillClasses = (hasValue, open) =>
  classnames(
    "btn d-flex align-items-center gap-2 shadow-none paragraph-sm fw-medium px-3 py-2",
    hasValue
      ? "bg-primary-10 border border-primary-70 text-primary-70 rounded-2 chip-toggle-active"
      : "btn-outline-secondary border-neutral-20",
    { show: open },
  );

const dropdownStyle = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  zIndex: 200,
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
  padding: 12,
  minWidth: 200,
  maxWidth: "calc(100vw - 16px)",
  maxHeight: 300,
  overflowY: "auto",
};

const backdropStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15,20,36,0.4)",
  zIndex: 1050,
};

const sheetStyle = {
  position: "fixed",
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1051,
  background: "#fff",
  borderRadius: "16px 16px 0 0",
  boxShadow: "0 -4px 24px rgba(10,13,18,0.18)",
  maxHeight: "85vh",
  flexDirection: "column",
};

function FilterPill({ label, attribute, active: activeProp = false, activeText = null, bodyScroll = false, children }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const panelRef = useRef(null);
  const [leftOffset, setLeftOffset] = useState(0);
  const isMobile = useIsMobile();

  // bodyScroll: the child owns its height/scroll/padding, so the dropdown chrome adds none.
  const panelStyle = bodyScroll
    ? { ...dropdownStyle, padding: 0, maxHeight: "none", overflow: "visible" }
    : dropdownStyle;

  useLayoutEffect(() => {
    if (isMobile || !open || !ref.current || !panelRef.current) return;
    const wrapperLeft = ref.current.getBoundingClientRect().left;
    const panelWidth = panelRef.current.offsetWidth;
    const overshoot = wrapperLeft + panelWidth - (window.innerWidth - 8);
    setLeftOffset(overshoot > 0 ? -overshoot : 0);
  }, [open, isMobile]);

  const attrs = attribute ? [attribute].flat() : [];
  const { items } = useCurrentRefinements({ includedAttributes: attrs });
  let activeLabels = [];
  if (attribute) {
    activeLabels = items.flatMap((i) => i.refinements.map((r) => r.label));
  } else if (activeText) {
    activeLabels = [activeText];
  }

  const hasRefinements = activeLabels.length > 0;
  const hasValue = hasRefinements || activeProp;

  // Desktop only: the mobile sheet is portaled out of this subtree, so an outside-click closer
  // would fire on taps inside it.
  useEffect(() => {
    if (!open || isMobile) return undefined;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open, isMobile]);

  useEffect(() => {
    if (!open || !isMobile) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, isMobile]);

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        className={pillClasses(hasValue, open)}
        style={{ whiteSpace: "nowrap" }}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
        {activeLabels.length > 0 ? `: ${activeLabels.join(", ")}` : ""}
        <i className="fa-solid fa-chevron-down paragraph-sm fw-normal" />
      </button>
      {/* Always mounted — CSS hide so Algolia widgets never unmount and lose their state */}
      {isMobile ? (
        createPortal(
          <>
            {open && <div style={backdropStyle} onClick={() => setOpen(false)} aria-hidden="true" />}
            <div ref={panelRef} style={{ ...sheetStyle, display: open ? "flex" : "none" }}>
              <div style={{ display: "flex", justifyContent: "center", paddingTop: 8 }}>
                <div style={{ width: 36, height: 4, borderRadius: 2, background: "#d2d3d4" }} />
              </div>
              <div
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 16px" }}
              >
                <span style={{ fontSize: 15, fontWeight: 700, color: "#212528" }}>{label}</span>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  style={{
                    border: "none",
                    background: "none",
                    color: "#533dd1",
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: "pointer",
                    padding: 4,
                  }}
                >
                  Done
                </button>
              </div>
              <div
                style={
                  bodyScroll
                    ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }
                    : { flex: 1, minHeight: 0, overflowY: "auto", padding: "0 16px 16px" }
                }
              >
                {children}
              </div>
            </div>
          </>,
          document.body,
        )
      ) : (
        <div ref={panelRef} style={{ ...panelStyle, left: leftOffset, display: open ? "block" : "none" }}>
          {children}
        </div>
      )}
    </div>
  );
}

FilterPill.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  attribute: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  active: PropTypes.bool,
  activeText: PropTypes.string,
  bodyScroll: PropTypes.bool,
};

export default FilterPill;
